/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { renderHomePageHeader } from "../../assets/component/renderHomePageHeader.js";
import HomepageMenuEn from "../../assets/component/HomepageMenuEn/HomepageMenuEn.js";
import FaqSections from "../../assets/component/FaqSections/FaqSections.js";

const GROUPS = [
  {
    id: "start",
    title: "Getting started",
    note: "Opening an account and going live.",
    items: [
      {
        q: "What is CheckPlus?",
        a: "CheckPlus is a Hong Kong payment and donation platform built for nonprofits. It gives your organisation an online account, a payment landing page and automated receipting, so you can collect payments and donations without building or maintaining your own system.",
      },
      {
        q: "Who can sign up?",
        a: "Charitable organisations registered in Hong Kong, including those with tax-exempt status under section 88 of the Inland Revenue Ordinance, as well as schools, churches, associations and social enterprises that collect payments or donations.",
      },
      {
        q: "How long does it take to get set up?",
        a: "Most organisations create an account and publish their first payment or donation page the same day. Verification of your organisation and bank details is handled alongside your payment provider and usually completes within a few business days.",
      },
      {
        q: "Do we need a developer?",
        a: "No. Pages, payment items, campaigns and receipts are all configured from your CheckPlus account. There is nothing to install and no code to write.",
      },
      {
        q: "What do we need to prepare?",
        a: "Your organisation’s registration documents, the bank account that should receive settlements, an authorised contact person, and the text and images you would like on your payment page.",
      },
    ],
  },
  {
    id: "payments",
    title: "Payments & fees",
    note: "Payment methods, platform fees and settlement.",
    items: [
      {
        q: "Which payment methods can we accept?",
        a: "Visa, MasterCard, American Express and UnionPay are supported on every plan through Stripe. Apple Pay, Google Pay and PayMe for Business are available on the Pro plan, and additional local and Mainland methods can be enabled depending on your account.",
      },
      {
        q: "What does CheckPlus cost?",
        a: (
          <span>
            The Free plan is HK$0 for registered Hong Kong charitable
            organisations. Plus is HK$459 per month billed annually (HK$550
            billed monthly) and Pro is HK$792 per month billed annually (HK$950
            billed monthly). See the <Link to="/EnPricing">pricing page</Link>
            {" for the full comparison."}
          </span>
        ),
      },
      {
        q: "What is the platform fee?",
        a: "The platform fee is 2% on the Free plan and 1% on the Plus and Pro plans. Payment processing fees are charged separately by the payment gateway.",
      },
      {
        q: "Can donors cover the transaction fee?",
        a: "Yes. On every plan you can let donors choose to cover the transaction fee, so your organisation receives the full intended amount.",
      },
      {
        q: "When do funds reach our bank account?",
        a: "Funds are settled by the payment provider directly to your organisation’s bank account on its standard payout schedule. CheckPlus never holds your funds.",
      },
      {
        q: "Can we accept recurring monthly donations?",
        a: "Yes — monthly recurring transactions are available on the Pro plan, including automatic receipting for each successful payment.",
      },
    ],
  },
  {
    id: "receipts",
    title: "Donations & receipts",
    note: "Receipting, reporting and donor records.",
    items: [
      {
        q: "Are receipts issued automatically?",
        a: "Yes. Payment and donation receipts are generated and emailed automatically as soon as a transaction succeeds, on every plan.",
      },
      {
        q: "Can receipts carry our own branding?",
        a: "Yes. Receipts use your organisation’s name and details, and Plus and Pro plans allow further customisation of your payment landing page and receipt presentation.",
      },
      {
        q: "Can we issue tax-deductible donation receipts?",
        a: "If your organisation holds tax-exempt status under section 88, CheckPlus can issue donation receipts with the information Hong Kong donors need for their tax filing. You remain responsible for the accuracy of what appears on the receipt.",
      },
      {
        q: "Can we reissue or correct a receipt?",
        a: "Yes. Receipts can be resent to a donor, and corrected records can be reissued from your account so your reporting stays accurate.",
      },
      {
        q: "What reporting do we get?",
        a: "Plus and Pro plans generate system reports covering transactions, donors and campaigns, which can be exported for your finance and audit records.",
      },
    ],
  },
  {
    id: "security",
    title: "Security & privacy",
    note: "How your data and your donors’ data are handled.",
    items: [
      {
        q: "Where is our data stored?",
        a: "Data is stored on Amazon Web Services infrastructure with encryption in transit and at rest.",
      },
      {
        q: "Does CheckPlus store card numbers?",
        a: "No. Card details are captured and processed by Stripe, a PCI-DSS Level 1 certified provider. Full card numbers never touch CheckPlus systems.",
      },
      {
        q: "Will our donor data ever be sold?",
        a: (
          <span>
            No. CheckPlus has pledged never to sell or share user data with any
            third-party company without specific consent. See our{" "}
            <Link to="/EnPrivacy">privacy commitment</Link>
            {"."}
          </span>
        ),
      },
      {
        q: "Who in our team can see what?",
        a: "Each organisation has a single account at the moment, so everyone who signs in sees the same information. Team accounts, with a separate login and its own access for each staff member, are coming soon.",
      },
    ],
  },
  {
    id: "account",
    title: "Account & support",
    note: "Changing plans, languages and getting help.",
    items: [
      {
        q: "Can we change plans later?",
        a: "You can upgrade at any time. A downgrade takes effect at the end of your current billing period, so on an annual plan you stay on your current plan until the year is up.",
      },
      {
        q: "Is CheckPlus available in Chinese?",
        a: "Yes. The platform and your donor-facing pages are available in English, Traditional Chinese and Simplified Chinese — multi-language pages are included on the Pro plan.",
      },
      {
        q: "How do we get help?",
        a: (
          <span>
            Write to our Hong Kong team from the{" "}
            <Link to="/EnContact">contact page</Link>
            {". We typically reply in 2–4 business days."}
          </span>
        ),
      },
    ],
  },
];

class EnFaq extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
    };
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  showModal() {
    this.setState({
      showModal: true,
    });
  }

  closeModal() {
    this.setState({
      showModal: false,
    });
  }

  render() {
    return (
      <div className="Faq-view">
        <Helmet>
          <title>FAQ - CheckPlus</title>
        </Helmet>
        {!!this.state.showModal && (
          <HomepageMenuEn closeModal={this.closeModal} />
        )}
        {renderHomePageHeader(this)}

        <FaqSections
          title="How can we help?"
          sub="Answers to the questions Hong Kong nonprofits ask us most — about getting set up, payments, fees, receipts and keeping donor data safe."
          searchPlaceholder="Search the FAQ…"
          emptyText="No answers matched your search. Try a different word, or contact our team below."
          groups={GROUPS}
          cta={{
            title: "Still have a question?",
            txt: "Our Hong Kong team typically replies in 2–4 business days.",
            primary: "Contact us",
            to: "/EnContact",
          }}
        />

        <div className="Faq-footer">
          <div className="Faq-footerNav">
            <Link to="/EnTeam">
              <div className="Faq-footerNavItem">Our Founders</div>
            </Link>

            <Link to="/EnFaq">
              <div className="Faq-footerNavItem">FAQ</div>
            </Link>

            <Link to="/EnContact">
              <div className="Faq-footerNavItem">Contact Us</div>
            </Link>

            <Link to="/EnPrivacyPolicy">
              <div className="Faq-footerNavItem">Privacy Policy</div>
            </Link>

            <Link to="/EnTerms">
              <div className="Faq-footerNavItem">Terms and Conditions</div>
            </Link>
          </div>

          <div className="Faq-footer_text">
            Flat A, 5/F, Wah Lee Industrial Building
            <br />
            11 Sze Shan Street, Yau Tong, Kowloon, Hong Kong
            <br />
            <p />
            Copyright © 2026 CheckPlus Ltd. All Rights Reserved.
          </div>
        </div>
      </div>
    );
  }
}

export default EnFaq;
